import React, { useEffect, useRef, useState } from 'react'
import { Signature, CircleNotch, FloppyDisk, Eye, Code as CodeIcon } from '@phosphor-icons/react'
import { Button } from '@/components/ui/Button'
import { useSenderEmailStore } from '@/stores/senderEmailStore'
// Mirrors the backend transform (campaign_executor._newlines_to_br); shared
// with every other signature preview so they can never drift apart.
import { newlinesToBr } from '@/lib/signatureHtml'

interface Props {
  senderEmailId: number
  initialSignatureHtml?: string | null
  onDirtyChange?: (dirty: boolean) => void
  /**
   * Preview only: no editor, no Save. For a user who can see this mailbox
   * but is not assigned to it with edit rights (spec 5.6).
   */
  readOnly?: boolean
}

const PLACEHOLDER = `e.g.
Jane Doe
Head of Growth, Acme Inc.
jane@acme.com  |  acme.com`


const EmailSignatureSection: React.FC<Props> = ({ senderEmailId, initialSignatureHtml, onDirtyChange, readOnly = false }) => {
  const updateSenderEmail = useSenderEmailStore(s => s.updateSenderEmail)
  const [value, setValue] = useState<string>(initialSignatureHtml ?? '')
  const [mode, setMode] = useState<'edit' | 'preview'>('edit')
  const [isSaving, setIsSaving] = useState(false)
  const initialRef = useRef(initialSignatureHtml ?? '')

  useEffect(() => {
    const next = initialSignatureHtml ?? ''
    setValue(next)
    initialRef.current = next
  }, [initialSignatureHtml, senderEmailId])

  const isDirty = value !== initialRef.current

  // The parent passes an inline arrow, so onDirtyChange is a new function on
  // every parent render. Depending on it re-ran this effect (report false,
  // then report isDirty again) each time the parent rendered for any reason.
  // The ref keeps the latest callback without making it a dependency, so the
  // parent hears about a change exactly when the flag changes.
  const onDirtyChangeRef = useRef(onDirtyChange)
  onDirtyChangeRef.current = onDirtyChange

  useEffect(() => {
    onDirtyChangeRef.current?.(isDirty)
    return () => onDirtyChangeRef.current?.(false)
  }, [isDirty])

  const handleSave = async () => {
    setIsSaving(true)
    try {
      await updateSenderEmail(senderEmailId, { signature_html: value })
      initialRef.current = value
    } finally {
      setIsSaving(false)
    }
  }

  const handleReset = () => {
    setValue(initialRef.current)
  }

  const preview = (
    <div className="rounded-lg border border-dashed border-border/60 dark:border-white/[0.08] bg-background dark:bg-white/[0.02] p-3 min-h-[120px]">
      {value.trim() ? (
        <div
          className="text-sm text-foreground dark:text-[#e2e4e7]"
          style={{ fontFamily: 'Arial, Helvetica, sans-serif', lineHeight: 1.5 }}
          dangerouslySetInnerHTML={{ __html: newlinesToBr(value) }}
        />
      ) : (
        <p className="text-xs text-muted-foreground/70 italic">No signature set.</p>
      )}
    </div>
  )

  if (readOnly) {
    return (
      <div
        className="mt-4 p-4 bg-muted/30 dark:bg-white/[0.02] rounded-xl border border-border/50 dark:border-white/[0.08] space-y-3"
        data-testid="email-signature-readonly"
      >
        <div className="flex items-center gap-2">
          <Signature className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="duotone" />
          <h3 className="text-sm font-emphasis text-foreground dark:text-[#f7f8f8]">Email Signature</h3>
        </div>
        <p className="text-[11px] text-muted-foreground dark:text-[#8a8f98] leading-relaxed">
          Only the people this mailbox is assigned to with edit rights can change its signature.
        </p>
        {preview}
      </div>
    )
  }

  return (
    <div className="mt-4 p-4 bg-muted/30 dark:bg-white/[0.02] rounded-xl border border-border/50 dark:border-white/[0.08] space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Signature className="w-4 h-4 text-[#5e6ad2] dark:text-[#a5b4fc]" weight="duotone" />
          <h3 className="text-sm font-emphasis text-foreground dark:text-[#f7f8f8]">Email Signature</h3>
        </div>
        <div className="inline-flex rounded-md border border-border/60 dark:border-white/[0.08] overflow-hidden">
          <button
            type="button"
            onClick={() => setMode('edit')}
            className={`px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors ${
              mode === 'edit'
                ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]'
                : 'text-muted-foreground hover:bg-muted/40 dark:hover:bg-white/[0.04]'
            }`}
          >
            <CodeIcon className="w-3 h-3" /> Edit
          </button>
          <button
            type="button"
            onClick={() => setMode('preview')}
            className={`px-2.5 py-1 text-[11px] font-medium inline-flex items-center gap-1 transition-colors border-l border-border/60 dark:border-white/[0.08] ${
              mode === 'preview'
                ? 'bg-[#5e6ad2]/10 text-[#5e6ad2] dark:bg-[#5e6ad2]/[0.18] dark:text-[#a5b4fc]'
                : 'text-muted-foreground hover:bg-muted/40 dark:hover:bg-white/[0.04]'
            }`}
          >
            <Eye className="w-3 h-3" /> Preview
          </button>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground dark:text-[#8a8f98] leading-relaxed">
        Appended to every outgoing email from this account. Locked from edit inside workflow nodes —
        the signature is added server-side on send.
      </p>

      {mode === 'edit' ? (
        <textarea
          value={value}
          onChange={e => setValue(e.target.value)}
          placeholder={PLACEHOLDER}
          rows={6}
          spellCheck={false}
          className="w-full rounded-lg border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] bg-background dark:bg-white/[0.02] text-[13px] font-mono text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] px-3 py-2.5 leading-relaxed focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 transition-colors resize-none overflow-y-auto"
        />
      ) : (
        preview
      )}

      <div className="flex items-center justify-end gap-2">
        {isDirty && (
          <Button variant="ghost" size="sm" onClick={handleReset} disabled={isSaving}>
            Discard
          </Button>
        )}
        <Button
          variant="default"
          size="sm"
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          icon={isSaving ? <CircleNotch className="w-3.5 h-3.5 animate-spin" /> : <FloppyDisk className="w-3.5 h-3.5" />}
        >
          {isSaving ? 'Saving' : 'Save Signature'}
        </Button>
      </div>
    </div>
  )
}

export default EmailSignatureSection
